import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { nuclearn_api_url } from "src/App";
import { NucToast } from "src/reusable/components/Toast";

const BASE = "/products/cap-screening-automation";

/**
 * The active CAP 2.0 shadow template for a production template, or null.
 * 404 (template missing) is surfaced as an error; "no shadow" is data=null.
 */
export const useShadowTemplate = (templateId) =>
  useQuery(
    ["shadowTemplate", templateId],
    () =>
      axios
        .get(`${nuclearn_api_url}${BASE}/template/${templateId}/shadow`)
        .then((response) => response.data),
    { enabled: Boolean(templateId), retry: false },
  );

export const useShadowComparisonRecords = (
  templateId,
  {
    divergesOnly = false,
    automated = null,
    manualSample = null,
    order = "desc",
    limit = 50,
    offset = 0,
  } = {},
) =>
  useQuery(
    [
      "shadowComparisonRecords",
      templateId,
      divergesOnly,
      automated,
      manualSample,
      order,
      limit,
      offset,
    ],
    () => {
      const params = { diverges_only: divergesOnly, order, limit, offset };
      if (automated !== null) {
        params.automated = automated;
      }
      if (manualSample !== null) {
        params.manual_sample = manualSample;
      }
      return axios
        .get(
          `${nuclearn_api_url}${BASE}/template/${templateId}/shadow-comparison/records`,
          { params },
        )
        .then((response) => response.data);
    },
    { enabled: Boolean(templateId), keepPreviousData: true },
  );

export const shadowComparisonExportUrl = (templateId) =>
  `${nuclearn_api_url}${BASE}/template/${templateId}/shadow-comparison/export`;

export const downloadShadowComparisonExport = (templateId, divergesOnly) =>
  axios({
    url: shadowComparisonExportUrl(templateId),
    params: divergesOnly ? { diverges_only: true } : {},
    method: "GET",
    responseType: "blob",
  }).then((response) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "cap2-shadow-comparison.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  });

export const useShadowRecordFeedback = (
  templateId,
  sourceUid,
  enabled = true,
) =>
  useQuery(
    ["shadowRecordFeedback", templateId, sourceUid],
    () =>
      axios
        .get(
          `${nuclearn_api_url}${BASE}/template/${templateId}/shadow/records/${encodeURIComponent(sourceUid)}/feedback`,
        )
        .then((response) => response.data),
    { enabled: Boolean(templateId && sourceUid) && enabled },
  );

export const useSubmitShadowRecordFeedback = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ templateId, sourceUid, feedback }) =>
      axios
        .post(
          `${nuclearn_api_url}${BASE}/template/${templateId}/shadow/records/${encodeURIComponent(sourceUid)}/feedback`,
          feedback,
        )
        .then((response) => response.data),
    {
      onSuccess: (_data, { templateId, sourceUid }) => {
        queryClient.invalidateQueries([
          "shadowRecordFeedback",
          templateId,
          sourceUid,
        ]);
        // feedback_count on the table rows.
        queryClient.invalidateQueries(["shadowComparisonRecords", templateId]);
        NucToast({
          message: "Feedback recorded",
          type: "success",
          className: "cap2-feedback-success-toast",
        });
      },
      onError: (error) => {
        let message = "Failed to record feedback";
        const detail = error?.response?.data?.detail;
        if (typeof detail === "string") {
          message += `: ${detail}`;
        }
        NucToast({
          message,
          type: "error",
          className: "cap2-feedback-error-toast",
        });
      },
    },
  );
};

/**
 * Templates eligible to link as this template's CAP 2.0 shadow (unlinked LLM
 * templates sharing the target's ground-truth dataset).
 */
export const useShadowCandidates = (templateId, { enabled = true } = {}) =>
  useQuery(
    ["shadowCandidates", templateId],
    () =>
      axios
        .get(
          `${nuclearn_api_url}${BASE}/template/${templateId}/shadow/candidates`,
        )
        .then((response) => response.data),
    { enabled: Boolean(templateId) && enabled },
  );

export const useLinkShadowTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ templateId, shadowTemplateId }) =>
      axios
        .post(`${nuclearn_api_url}${BASE}/template/${templateId}/shadow`, {
          shadow_template_id: shadowTemplateId,
        })
        .then((response) => response.data),
    {
      onSuccess: (_data, { templateId }) => {
        queryClient.invalidateQueries(["shadowTemplate", templateId]);
        queryClient.invalidateQueries(["shadowCandidates", templateId]);
        NucToast({
          message: "CAP 2.0 shadow linked",
          type: "success",
          className: "cap2-link-success-toast",
        });
      },
      onError: (error) => {
        let message = "Failed to link shadow";
        const detail = error?.response?.data?.detail;
        if (typeof detail === "string") {
          message += `: ${detail}`;
        }
        NucToast({
          message,
          type: "error",
          className: "cap2-link-error-toast",
        });
      },
    },
  );
};

export const useShadowComparisonAccuracy = (templateId) =>
  useQuery(
    ["shadowComparisonAccuracy", templateId],
    () =>
      axios
        .get(
          `${nuclearn_api_url}${BASE}/template/${templateId}/shadow-comparison/accuracy`,
        )
        .then((response) => response.data),
    { enabled: Boolean(templateId) },
  );
